'use client';

/**
 * Brand Context
 *
 * Provides brand configuration and helpers throughout the React app.
 * Use this context to access brand-specific data, assets, and text.
 */

import React, { createContext, useContext, useMemo } from 'react';
import { BrandId, BRANDS, BrandConfig, DEFAULT_BRAND } from '@/constants/brands';
import { replaceBrandPlaceholders } from './placeholders';

interface BrandContextValue {
  brand: BrandConfig;
  brandId: BrandId;
  getBrandAsset: (assetName: string) => string;
  getBrandText: (key: string, defaultValue?: string) => string;
  replaceContentPlaceholders: (value: string) => string;
}

const BrandContext = createContext<BrandContextValue | undefined>(undefined);

interface BrandProviderProps {
  children: React.ReactNode;
  brandId?: BrandId;
}

export function BrandProvider({ children, brandId }: BrandProviderProps) {
  const currentBrandId = brandId || DEFAULT_BRAND;
  const brand = BRANDS[currentBrandId];

  const value = useMemo<BrandContextValue>(() => {
    return {
      brand,
      brandId: currentBrandId,

      /**
       * Get brand-specific asset path
       * @param assetName - Name of the asset (e.g., 'logo', 'favicon')
       * @returns Path to the brand-specific asset
       */
      getBrandAsset: (assetName: string): string => {
        return `/brands/${currentBrandId}/${assetName}`;
      },

      /**
       * Get brand-specific text/content
       * This is a placeholder - actual implementation should integrate with i18n
       * @param key - Translation key
       * @param defaultValue - Fallback value if key not found
       * @returns Brand-specific text
       */
      getBrandText: (key: string, defaultValue?: string): string => {
        // TODO: Integrate with i18n system
        // For now, return default value or key
        return defaultValue || key;
      },
      replaceContentPlaceholders: (value: string): string => {
        return replaceBrandPlaceholders(value, currentBrandId);
      },
    };
  }, [currentBrandId, brand]);

  return (
    <BrandContext.Provider value={value}>
      {children}
    </BrandContext.Provider>
  );
}

/**
 * Hook to access brand context
 * @throws Error if used outside of BrandProvider
 */
export function useBrand(): BrandContextValue {
  const context = useContext(BrandContext);

  if (context === undefined) {
    throw new Error('useBrand must be used within a BrandProvider');
  }

  return context;
}

/**
 * Hook to get brand ID without throwing if provider is missing
 * Useful for server components or edge cases
 */
export function useBrandId(): BrandId {
  const context = useContext(BrandContext);
  return context?.brandId || DEFAULT_BRAND;
}

/**
 * Hook to get brand asset path
 */
export function useBrandAsset(assetName: string): string {
  const { getBrandAsset } = useBrand();
  return getBrandAsset(assetName);
}
